import React, { useEffect, useState } from 'react';
import FindInviteSuccessForm from '@apps/guest/packages/layout-engine/components/UnlockDialog/FindInviteSuccessForm/FindInviteSuccessForm';
import { VerifyEmailForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/VerifyEmailForm';
import { FindInviteForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/FindInviteForm';
import { AskHostForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AskHostForm';
import { AskHostSuccessForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AskHostSuccessForm';
import { ContactHostForm, ContactHostSuccessForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/ContactHostForm';
import { UnlockForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockForm';
import { useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog.context';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { GuestTypes } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog';
import type { ManageUnlockDialogFormsProps } from '@apps/guest/packages/layout-engine/components/UnlockDialog/ManageUnlockDialogForms/ManageUnlockDialogForms.types';

export const ManageUnlockDialogForms: React.FC<ManageUnlockDialogFormsProps> = ({ unlockData, onDialogClose }) => {
  const { eventId, hasUnlocked, shouldSkipPasswordGate, setShowBackButton, onBackButtonClick, shouldDisplayFindInvite, findInviteSuccessBehavior, isSpecificGuestsUnlock } =
    unlockData ?? {};

  // Find Invite is an identify-by-name step, so it only makes sense for invite-backed unlocks: the
  // specificGuests swap, and Schedule (which navigates to the schedule on success). Every other entry
  // uses reload-in-place — generic password unlocks, including a password-protected RSVP page, have no
  // invitation to match by name and would dead-end, so they just unlock the event and reload in place.
  const offerFindInvite = shouldDisplayFindInvite && (isSpecificGuestsUnlock || findInviteSuccessBehavior !== 'reloadInPlace');

  const [showAskHost, setShowAskHost] = useState<boolean>(false);
  const [showAskHostSuccess, setShowAskHostSuccess] = useState<boolean>(false);
  // Contact-host is the FindInviteForm variant of "Ask the Host": a guest who
  // can't find themselves by name asks the host to reach out (guest-not-found
  // email), as opposed to the UnlockForm variant which requests the password.
  const [showContactHost, setShowContactHost] = useState<boolean>(false);
  const [showContactHostSuccess, setShowContactHostSuccess] = useState<boolean>(false);
  const [guest, setGuest] = useState<GuestTypes>({ firstName: '', lastName: '', personIdTag: '' });
  const [guestsFound, setGuestsFound] = useState<number>(0);

  const { tq } = useTranslation('guestUnlock');

  const { toast } = useToast();
  const { markUnlocked } = useUnlockDialogContext();

  // Manage onClick Dialog Back button
  useEffect(() => {
    onBackButtonClick(() => {
      setShowAskHost(false);
      setShowContactHost(false);
      setGuestsFound(0);
    });
  }, [onBackButtonClick]);

  // Manage Dialog Back button conditions
  useEffect(() => {
    setShowBackButton(false);
    if (guestsFound > 1 && !guest.personIdTag) setShowBackButton(true);
    if (showAskHost) setShowBackButton(true);
    if (showContactHost) setShowBackButton(true);
  }, [guest.personIdTag, guestsFound, setShowBackButton, showAskHost, showContactHost]);

  // CB Only show UnlockSuccess toast if we don't have to display FindInvite
  const unlockSuccessHandle = () => {
    // Flag the unlock for both flows; the dialog provider's close wrapper
    // reads this and triggers a full page reload when the dialog finally closes.
    markUnlocked();
    if (!offerFindInvite) {
      hideUnlockSuccessHandle();
      toast(tq`🙌 Yay! You can now access all event details.`, { position: 'bottom-center' });
    }
  };

  // CB Only works if we don't have to display FindInvite
  const hideUnlockSuccessHandle = () => {
    if (!offerFindInvite) {
      onDialogClose();
    }
  };

  // If we have a personIdTag it means that we have already unlocked the invitation
  if (guest.personIdTag) {
    return (
      <FindInviteSuccessForm
        eventId={eventId}
        guest={guest}
        onDialogClose={onDialogClose}
        findInviteSuccessBehavior={findInviteSuccessBehavior}
      />
    );
  }

  // If we have more thant 1 guest found, it means that we have to verify the email.
  if (guestsFound > 1) {
    return (
      <VerifyEmailForm
        eventId={eventId}
        guest={guest}
        setGuest={setGuest}
      />
    );
  }

  // Contact-host is reachable from the FindInviteForm's "Ask the Host" link, so
  // its branches must precede the FindInviteForm branch (which otherwise wins).
  if (showContactHost) {
    const onCloseHandler = () => {
      setShowContactHost(false);
      setShowContactHostSuccess(true);
    };
    return (
      <ContactHostForm
        eventId={eventId}
        onClose={onCloseHandler}
      />
    );
  }

  if (showContactHostSuccess) {
    return <ContactHostSuccessForm onClose={onDialogClose} />;
  }

  if (offerFindInvite && (hasUnlocked || shouldSkipPasswordGate)) {
    return (
      <FindInviteForm
        eventId={eventId}
        setGuestsFound={setGuestsFound}
        setGuest={setGuest}
        onShowAskHost={() => setShowContactHost(true)}
      />
    );
  }

  // showAskHost Section
  if (showAskHost) {
    const onCloseHandler = () => {
      setShowAskHost(false);
      setShowAskHostSuccess(true);
    };
    return (
      <AskHostForm
        eventId={eventId}
        onClose={onCloseHandler}
      />
    );
  }

  if (showAskHostSuccess) {
    return <AskHostSuccessForm onClose={onDialogClose} />;
  }

  // Show password form to unlock the event
  return (
    <UnlockForm
      eventId={eventId}
      onUnlockSuccess={unlockSuccessHandle}
      onShowAskHost={() => setShowAskHost(true)}
    />
  );
};
